/**
 * Farben und Maße der **Memo** — der zweiten Art von **Canvas-Element**.
 *
 * Eine eigene Palette neben der des **Notiz**, weil eine Notizfarbe VIER
 * Werte braucht: Fläche, Rand, Text und die Pille des Autors. Die Notiz
 * kommt mit einem aus (seine Fläche), weil sein Text immer schwarz auf hell
 * steht und er keinen Rand trägt.
 *
 * **Dieselben Schlüssel, dieselbe Reihenfolge** wie `CANVAS_NOTE_COLORS`: Ein
 * Farbwechsel soll auf beiden Arten dieselbe Farbe meinen, und der
 * Weiterschalt-Knopf in der Options-Leiste läuft über den Index.
 *
 * Bewusst ohne React-Bezug, wie die übrigen `utils/`.
 */

import { DEFAULT_CANVAS_NOTE_COLOR, type CanvasNoteColorKey } from './canvasColors'
import { DEFAULT_CANVAS_NOTE_HEIGHT } from './canvasNoteSize'

export interface TextNoteColor {
  key: CanvasNoteColorKey
  /** Die Fläche des Zettels (Tailwind-Stufe 50). */
  surface: string
  /** Der 1px-Rand ringsum (300–400). */
  border: string
  /** Titel und Text (950) — dunkel genug, um auf der Fläche zu tragen. */
  text: string
  /** Der Grund der Autoren-Pille (100). */
  chip: string
  /**
   * Der **Streifen** am linken Rand — der Rücken des Blocks, von dem das
   * Blatt abgerissen wurde (Figma `5379:182781`).
   *
   * Aus `surface` GERECHNET und dann als Hex festgeschrieben, damit die
   * Werte nachlesbar bleiben wie die übrige Palette. Das Maß stammt aus dem
   * Referenzpaar Orange (`orange/50` → `orange/100`), in OKLCH gemessen:
   * Helligkeit −0,027, Buntheit +0,019.
   *
   * In OKLCH, weil dort das Absenken der Helligkeit den Farbton nicht
   * mitverschiebt — dieselbe Begründung wie beim Eselsohr (siehe `fold`).
   * Der Zuschlag auf die Buntheit ist AUFGESCHLAGEN, nicht multipliziert:
   * Ein Faktor verstärkte die Unterschiede der Palette, statt sie gleich zu
   * behandeln.
   *
   * **Nicht `chip`**, obwohl beide eine Stufe unter dem Papier liegen: Bei
   * Grau ist der Pillenton heller als die Fläche, der Streifen wäre dort
   * eine Aufhellung statt einer Vertiefung.
   */
  stripe: string
  /**
   * Die 1,5px feine Trennlinie am rechten Ende des Streifens — dieselbe
   * Rechnung, zwei Stufen tiefer (Helligkeit −0,080, Buntheit +0,054; aus
   * `orange/50` → `orange/200`).
   *
   * Sie liegt INNERHALB der 12px des Streifens, wie in Figma, wo er
   * `w-[12px]` mit einem `border-right` ist.
   */
  stripeLine: string
  /**
   * Der Knick des **Eselsohrs** — nur bei den TONLOSEN Farben.
   *
   * Sonst wird er aus der Fläche gerechnet (`oklch(from … calc(l - 0.08)
   * calc(c + 0.03) h)` in `MemoNode.scss`): eine Spur dunkler und
   * bunter, damit jede Farbe gleich weit vom Papier abrückt. Weiß und Grau
   * haben aber keinen Farbton, an dem sich die Buntheit festmachen könnte —
   * der Zuschlag landete bei Hue 0, und der Knick wurde rosa. Sie bringen
   * ihren Ton deshalb mit: schlichtes Grau, bei Weiß heller.
   */
  fold?: string
  /**
   * Derselbe Knick beim Überfahren des Ohrs — eine Stufe tiefer. Muss
   * mitgeführt werden, wo `fold` es wird: Der Wert steht am Zettel als
   * Inline-Variable, und die schlägt jede Regel aus der SCSS, auch die
   * Hover-Regel.
   */
  foldHover?: string
}

/**
 * Die neun Farben — **dieselben Schlüssel und dieselbe Reihenfolge** wie
 * `CANVAS_NOTE_COLORS`. Ein Farbwechsel soll auf beiden Arten dieselbe Farbe
 * meinen, und der Farbwähler zeigt beide Paletten in einer Reihe.
 *
 * `surface` ist durchweg aus dem Figma GEMESSEN (Node `5083:51824`). Die
 * übrigen drei Werte folgen dem Muster aus den Tailwind-Skalen — Rand 300,
 * Text 950, Pille 100 —, und die der sechs alten Schlüssel stehen unverändert
 * so, wie sie beim ersten Entwurf gemessen wurden.
 *
 * Das Memo ist heller als der **Notiz** derselben Farbe: Auf ihr steht
 * dunkler Text über mehrere Zeilen, auf ihm ein kurzer Einfall.
 *
 * Die Werte stehen FEST und nicht als `--colors-*`: Die Legacy-Leiter flippt
 * im Dark-Mode (CLAUDE.md), und ein Memo behält ihre Farbe. Dieselbe
 * Begründung wie bei `CANVAS_NOTE_COLORS`.
 */
/*
  **Die dunkle Stufe** jeder Farbe (`-dark`, direkt hinter der hellen, wie in
  `CANVAS_NOTE_COLORS`): Fläche eine Tailwind-Stufe 200, Rand 400, Pille 300 —
  ein Mittelton, auf dem derselbe dunkle Text trägt. Weiß-dark und Grau-dark
  bringen wie ihre hellen Schwestern einen eigenen Knick mit.
*/
/*
  **Streifen und Linie bei den TONLOSEN Farben** (Weiß und Grau, je hell und
  dunkel): nur die Helligkeit abgesenkt, kein Zuschlag auf die Buntheit.
  Dieselbe Falle wie beim Eselsohr — ein Zuschlag landete bei Hue 0, und der
  Streifen wurde rosa.

  **Dafür sinkt die Helligkeit dort tiefer** (−0,050 / −0,100 statt −0,027 /
  −0,080). Bei den bunten Farben tragen zwei Größen den Abstand, bei den
  tonlosen nur eine — mit den Werten der bunten verschwand der Streifen auf
  Weiß vollständig (#f6f6f6 auf #ffffff war im Bild nicht zu finden). Die
  eine verbleibende Größe muss deshalb mehr leisten.

  **Und wo eine Farbe aus sRGB herausläuft**, wird die Buntheit
  zurückgenommen, nicht der Kanal abgeschnitten: Abschneiden verschiebt den
  Farbton, bei Rot sichtbar als Rosa. Betrifft Rot, Orange und Pink.
*/
export const CANVAS_MEMO_COLORS: readonly TextNoteColor[] = [
  { key: 'white', surface: '#ffffff', border: '#d4d4d4', text: '#0a0a0a', chip: '#f5f5f5',
    stripe: '#eeeeee', stripeLine: '#dedede', fold: '#ebebeb', foldHover: '#dcdcdc' },
  { key: 'white-dark', surface: '#e8e8e8', border: '#a3a3a3', text: '#0a0a0a', chip: '#d9d9d9',
    stripe: '#d8d8d8', stripeLine: '#c7c7c7', fold: '#d4d4d4', foldHover: '#c4c4c4' },
  { key: 'gray', surface: '#f2f2f2', border: '#d4d4d8', text: '#09090b', chip: '#f4f4f5',
    stripe: '#e1e1e1', stripeLine: '#d1d1d1', fold: '#dedede', foldHover: '#cfcfcf' },
  { key: 'gray-dark', surface: '#dcdce0', border: '#a1a1aa', text: '#09090b', chip: '#c8c8cd',
    stripe: '#cccccc', stripeLine: '#bcbcbc', fold: '#c8c8cc', foldHover: '#b9b9be' },
  // Beige braucht keinen eigenen Knick: Es HAT einen Farbton, die Rechnung
  // greift (siehe `fold`).
  { key: 'beige', surface: '#fdfaf3', border: '#d9c9a8', text: '#33291b', chip: '#f2e8d5',
    stripe: '#f9f1dc', stripeLine: '#f2deb0' },
  { key: 'beige-dark', surface: '#f1e2c4', border: '#c4a574', text: '#33291b', chip: '#e3cfa6',
    stripe: '#eed8ad', stripeLine: '#e7c57f' },
  { key: 'red', surface: '#fff0f0', border: '#fca5a5', text: '#450a0a', chip: '#fee2e2',
    stripe: '#ffe4e4', stripeLine: '#ffcbcb' },
  { key: 'red-dark', surface: '#fecaca', border: '#f87171', text: '#450a0a', chip: '#fca5a5',
    stripe: '#ffbcbd', stripeLine: '#ffa1a3' },
  { key: 'orange', surface: '#fff6ed', border: '#fdba74', text: '#431407', chip: '#ffedd5',
    stripe: '#ffebd7', stripeLine: '#fdd5ad' },
  { key: 'orange-dark', surface: '#fed7aa', border: '#fb923c', text: '#431407', chip: '#fdba74',
    stripe: '#fdcc92', stripeLine: '#f8b662' },
  { key: 'green', surface: '#f1fdf4', border: '#86efac', text: '#052e16', chip: '#dcfce7',
    stripe: '#dff8e5', stripeLine: '#bbedc9' },
  { key: 'green-dark', surface: '#bbf7d0', border: '#4ade80', text: '#052e16', chip: '#86efac',
    stripe: '#a6f2c2', stripeLine: '#7be6a7' },
  { key: 'yellow', surface: '#fffae8', border: '#d6d0b8', text: '#431407', chip: '#faeed6',
    stripe: '#faf1d1', stripeLine: '#f0dfa4' },
  { key: 'yellow-dark', surface: '#fdf0b5', border: '#c9b96a', text: '#431407', chip: '#f4e28f',
    stripe: '#f8e79c', stripeLine: '#edd569' },
  { key: 'blue', surface: '#e4fbff', border: '#67c3d6', text: '#083344', chip: '#d2eef9',
    stripe: '#ccf6fd', stripeLine: '#9cebf8' },
  { key: 'blue-dark', surface: '#cfeafd', border: '#4cbdf3', text: '#083344', chip: '#a8dcfb',
    stripe: '#bbe3ff', stripeLine: '#95d4ff' },
  { key: 'indigo', surface: '#f0f3ff', border: '#a5b4fc', text: '#1e1b4b', chip: '#e0e7ff',
    stripe: '#e4eaff', stripeLine: '#ced7ff' },
  { key: 'indigo-dark', surface: '#dde3fd', border: '#818cf8', text: '#1e1b4b', chip: '#c7d2fe',
    stripe: '#d1d9ff', stripeLine: '#bbc7ff' },
  { key: 'purple', surface: '#faf5fe', border: '#d8b4fe', text: '#3b0764', chip: '#f3e8ff',
    stripe: '#f5e9ff', stripeLine: '#ebd1ff' },
  { key: 'purple-dark', surface: '#efe3fd', border: '#c084fc', text: '#3b0764', chip: '#d8b4fe',
    stripe: '#e9d7fe', stripeLine: '#dec0fe' },
  { key: 'pink', surface: '#fdf2f8', border: '#f9a8d4', text: '#500724', chip: '#fce7f3',
    stripe: '#fee4f2', stripeLine: '#fdcae7' },
  { key: 'pink-dark', surface: '#fcdcec', border: '#f472b6', text: '#500724', chip: '#f9a8d4',
    stripe: '#fccee6', stripeLine: '#fbb3d9' },
] as const

/**
 * Die Farbe einer Memo. Unbekannte Schlüssel fallen auf **Gelb** zurück —
 * nicht auf den ersten Eintrag: Vorn steht seit der Erweiterung auf neun
 * Farben Weiß, und ein Memo ohne erkennbare Farbe soll aussehen wie früher.
 */
export function getMemoColor(key: string | null): TextNoteColor {
  return CANVAS_MEMO_COLORS.find(c => c.key === key)
    ?? CANVAS_MEMO_COLORS.find(c => c.key === DEFAULT_CANVAS_NOTE_COLOR)!
}

/**
 * Die Breite einer frisch abgesetzten Memo (Figma: `w-[249px]`).
 *
 * Deutlich breiter als eine Notiz (168px): Auf ein Memo soll Text passen,
 * und bei 15px Schrift trägt diese Breite gut 30 Zeichen je Zeile.
 */
export const DEFAULT_MEMO_WIDTH = 249

/**
 * Die Grenzen beim Ziehen der Breite.
 *
 * Nur die Obergrenze teilt sich mit der Notiz (`MAX_CANVAS_NOTE_SIZE`, 648).
 *
 * Die Untergrenze ist NICHT `MIN_CANVAS_NOTE_SIZE` (72): Eine schmal
 * gezogene, leere Memo soll nie kleiner wirken als das Element, von dem sie
 * sich unterscheiden soll. Lange war sie deshalb genau die Kantenlänge einer
 * Notiz (168, `DEFAULT_CANVAS_NOTE_WIDTH`).
 *
 * **Seit dem Streifen liegt sie darüber.** Das Polster ist auf 24px
 * gewachsen und der Streifen nimmt links weitere 12px — bei 168px blieben
 * nur 107px für den Text, schmaler als jede Zeile, die man lesen will. 200px
 * lassen 139px und gleichen das neue Polster damit aus (vorher waren es
 * 134). Der Gedanke bleibt derselbe, nur mit Abstand statt auf Kante: Ein
 * Memo ist breiter als eine Notiz, und das soll man auch an der Untergrenze
 * sehen.
 *
 * Es ist die Ziehgrenze, keine Bereinigung — bereits schmaler gespeicherte
 * Memos bleiben, wie sie sind, und rasten erst beim nächsten Ziehen ein.
 */
export const DEFAULT_MEMO_MIN_WIDTH = 200
export const DEFAULT_MEMO_MAX_WIDTH = 648

/**
 * Wie hoch eine LEERE Memo mindestens ist — so hoch wie ein **Notiz**.
 *
 * Als Mindestmaß und nicht als Höhe: Das Memo schrumpft nie darunter, wächst
 * aber von dort nach unten. Eine leere Memo sieht damit aus wie ein Zettel,
 * auf den etwas soll, und nicht wie ein Strich, der erst beim Tippen zum
 * Zettel wird.
 *
 * Über `DEFAULT_CANVAS_NOTE_HEIGHT` und nicht als eigene 168: Die beiden
 * Arten sollen frisch abgesetzt gleich hoch beginnen, und eine zweite Zahl
 * liefe irgendwann auseinander.
 */
export const MEMO_MIN_HEIGHT = DEFAULT_CANVAS_NOTE_HEIGHT

